import {
  numberToPercentWithFixedFractionDigits,
  parseFixedFractionDigits,
} from '../helpers/number';
import { applyTimezone, getDateOnly, parseDate } from './../helpers/date';

export enum SIMULATION_TYPE {
  SIMPLE = 'Simples',
  COMPLETE = 'Completa',
}

export class SimulationParameters {
  simulationType: SIMULATION_TYPE;
  debtTransfer: boolean;
  debtTransferAmount: number;
  releasedMarginAmount: number;
  financialChangeAmount: number;
  amount: number;
  numberOfInstallments: number;
  interestRate: number;
  fee: number;
  isFeePercentage: boolean;
  checkEmployeeLimits: boolean;
  cutoffDay: number;
  minimumDueDate: Date;

  constructor(data: SimulationParameters | DjangoSimulationParameters = {}) {
    if (data instanceof SimulationParameters) {
      bindSimulationParametersData(this, data);
    } else {
      bindDjangoSimulationParametersData(this, data);
    }
  }

  get djangoModel() {
    return getDjangoModel(this);
  }
}

export function parseSimulationParameters(
  data?: SimulationParameters | DjangoSimulationParameters,
) {
  if (data) {
    return new SimulationParameters(data);
  } else {
    return undefined;
  }
}

function bindSimulationParametersData(
  simulationParameters: SimulationParameters,
  data: SimulationParameters,
) {
  simulationParameters.amount = data.amount;
  simulationParameters.numberOfInstallments = data.numberOfInstallments;
  simulationParameters.interestRate = data.interestRate;
  simulationParameters.fee = data.fee;
  simulationParameters.isFeePercentage = data.isFeePercentage;
  simulationParameters.checkEmployeeLimits = data.checkEmployeeLimits;
  simulationParameters.cutoffDay = data.cutoffDay;
  simulationParameters.minimumDueDate = data.minimumDueDate;
  simulationParameters.simulationType = data.simulationType;
  simulationParameters.debtTransfer = data.debtTransfer;
  simulationParameters.debtTransferAmount = data.debtTransferAmount;
  simulationParameters.financialChangeAmount = data.financialChangeAmount;
  simulationParameters.releasedMarginAmount = data.releasedMarginAmount;
}

///////////////////
// DJANGO SUPPOT //
///////////////////
export interface DjangoSimulationParameters {
  simulation_type?: SIMULATION_TYPE;
  debt_transfer?: boolean;
  debt_transfer_amount?: number;
  released_margin_amount?: number;
  financial_change_amount?: number;
  amount?: number;
  number_of_installments?: number;
  interest_rate?: string;
  fee?: string;
  is_fee_percentage?: boolean;
  checkEmployeeLimits?: boolean;
  cutoff_day?: number;
  minimum_due_date?: string;
}

function bindDjangoSimulationParametersData(
  simulationParameters: SimulationParameters,
  data: DjangoSimulationParameters,
) {
  simulationParameters.amount = data.amount;
  simulationParameters.numberOfInstallments = data.number_of_installments;
  simulationParameters.interestRate = parseFloat(data.interest_rate) * 100;
  simulationParameters.fee = parseFloat(data.fee) * 100;
  simulationParameters.isFeePercentage = data.is_fee_percentage;
  simulationParameters.checkEmployeeLimits = data.checkEmployeeLimits;
  simulationParameters.cutoffDay = data.cutoff_day;
  simulationParameters.minimumDueDate = applyTimezone(parseDate(data.minimum_due_date), 0);
  simulationParameters.simulationType = data.simulation_type;
  simulationParameters.debtTransfer = data.debt_transfer;
  simulationParameters.debtTransferAmount = data.debt_transfer_amount;
  simulationParameters.financialChangeAmount = data.financial_change_amount;
  simulationParameters.releasedMarginAmount = data.released_margin_amount;
}

function getDjangoModel(simulationParameters: SimulationParameters): DjangoSimulationParameters {
  const isFeePercentage: boolean = !!simulationParameters.isFeePercentage;
  const checkEmployeeLimits: boolean = !!simulationParameters.checkEmployeeLimits;
  return {
    simulation_type: simulationParameters.simulationType,
    debt_transfer: simulationParameters.debtTransfer,
    debt_transfer_amount: simulationParameters.debtTransferAmount,
    released_margin_amount: simulationParameters.releasedMarginAmount,
    financial_change_amount: simulationParameters.financialChangeAmount,
    amount: simulationParameters.amount,
    number_of_installments: simulationParameters.numberOfInstallments,
    interest_rate: numberToPercentWithFixedFractionDigits(simulationParameters.interestRate, 6),
    fee: isFeePercentage
      ? numberToPercentWithFixedFractionDigits(simulationParameters.fee, 6)
      : parseFixedFractionDigits(simulationParameters.fee, 6),
    is_fee_percentage: isFeePercentage,
    checkEmployeeLimits: checkEmployeeLimits,
    cutoff_day: simulationParameters.cutoffDay,
    minimum_due_date: simulationParameters.minimumDueDate
      ? getDateOnly(simulationParameters.minimumDueDate)
      : null,
  };
}
